/* ============================================================
   CHRONCES — <cr-infinite-grid>
   Défilement infini de la grille de collection.

   La pagination numérotée reste RENDUE dans le HTML : c'est elle
   qui fait fonctionner la page sans JavaScript (et pour les
   robots d'indexation, qui suivent les liens). Le composant ne la
   masque qu'une fois qu'il a pris la main, et la remet en place
   si un chargement échoue.

   Les pages suivantes sont demandées à la **Section Rendering
   API** (`?section_id=`) plutôt qu'en page entière : on ne
   rapatrie que la grille, pas l'en-tête, le pied et le pop-up.
   ============================================================ */

class CrInfiniteGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-cr-grid]');
    this.status = this.querySelector('[data-cr-status]');
    this.sentinel = this.querySelector('[data-cr-sentinel]');
    this.pagination = this.querySelector('[data-cr-pagination]');
    this.retry = this.querySelector('[data-cr-retry]');

    if (!this.grid || !this.sentinel) return;

    this.retry?.addEventListener('click', this.onRetry);

    // Rien à charger : on laisse la page telle quelle.
    if (!this.dataset.next) return;

    // On ne masque la pagination qu'ici : si le script n'a pas tourné,
    // elle reste le seul moyen de voir la suite du catalogue.
    this.pagination?.setAttribute('hidden', '');

    this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '600px 0px' });
    this.observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.retry?.removeEventListener('click', this.onRetry);
  }

  onIntersect = (entries) => {
    if (entries.some((entry) => entry.isIntersecting)) this.load();
  };

  onRetry = () => {
    this.retry?.setAttribute('hidden', '');
    this.load();
  };

  async load() {
    if (this.busy) return;
    const next = this.dataset.next;
    if (!next) return;

    this.busy = true;
    this.setStatus(this.dataset.loadingText || 'Chargement…');

    try {
      const url = new URL(next, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString());
      // 🚨 Vérifier `ok` AVANT de lire le corps : sur une erreur Shopify
      // renvoie du HTML, et un parse direct masque la cause (§12).
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const source = doc.querySelector('cr-infinite-grid');
      const cards = source?.querySelector('[data-cr-grid]');
      if (!cards) throw new Error('grille absente de la réponse');

      // `append(...children)` DÉPLACE les noeuds : les composants
      // personnalisés (<cr-add-to-cart>) s'initialisent à l'insertion.
      this.grid.append(...cards.children);

      this.dataset.next = source.dataset.next || '';

      if (this.dataset.next) {
        this.setStatus('');
        // 🚨 En descendant vite, les intersections survenues pendant la
        // requête sont perdues (le verrou `busy` les a écartées) et
        // l'`IntersectionObserver` ne rejoue QUE sur un changement d'état.
        // Si le repère est encore dans la zone après le rendu, on enchaîne :
        // sinon le catalogue s'arrête tant qu'on ne redéfile pas.
        // ⚠️ Pas de `requestAnimationFrame` ici : il ne part pas dans un
        // onglet qui ne compose pas de frame (piège de test connu), et le
        // vrai garde-fou est `sentinelInRange()`, pas le rythme d'affichage.
        if (this.sentinelInRange()) setTimeout(() => this.load(), 0);
      } else {
        // Plus rien à charger : on arrête d'observer et on le dit.
        this.observer?.disconnect();
        this.pagination?.remove();
        this.setStatus(this.dataset.endText || '');
      }
    } catch (error) {
      console.error('[cr-infinite-grid]', error);
      this.observer?.disconnect();
      // Repli : on rend la pagination et un bouton explicite. Mieux vaut
      // deux clics qu'un catalogue qui s'arrête sans rien dire.
      this.pagination?.removeAttribute('hidden');
      this.retry?.removeAttribute('hidden');
      this.setStatus('');
    } finally {
      this.busy = false;
    }
  }

  sentinelInRange() {
    if (!this.sentinel) return false;
    const { top } = this.sentinel.getBoundingClientRect();
    return top <= window.innerHeight + 600;
  }

  setStatus(text) {
    if (!this.status) return;
    this.status.textContent = text;
    this.status.toggleAttribute('hidden', text === '');
  }
}

if (!customElements.get('cr-infinite-grid')) {
  customElements.define('cr-infinite-grid', CrInfiniteGrid);
}
